/* eslint-disable no-undef */
import {StyleSheet, useWindowDimensions} from 'react-native';
import {useMemo} from 'react';
import {usePageTheme} from './usePageTheme';
import {usePageLocale} from "../locale/usePageLocale";
import {useTheme} from "./useTheme";

/** @typedef {'row' | 'row-reverse'} FlexDirection */

/**
 * @typedef {object} GetStylesParam
 * @property {FlexDirection} ltrFlexDirection
 * @property {number} width
 * @property {number} height
 * @property {import('./shadow').Shadow} shadow
 * @property {import('./colors').Colors} colors
 */

/**
 * @template T
 * @typedef {() => GetStylesParam & {styles: T}} UseStyle
 */

/**
 * @template T
 * @callback GetStyles
 * @param {GetStylesParam} params
 * @returns {import('react-native').StyleSheet.NamedStyles<T>}
 */

/**
 * @template T
 * @param {GetStyles<T>} getStyles
 * @returns {UseStyle<T>}
 */
export const createUseStyle = (getStyles) => {
    return (...arg) => {
        const {theme, colors, size, shadow, contentWidth} = useTheme();
        const {width, height} = useWindowDimensions();
        const pageName = window.location.href.split('/')[3];
        const {themeId, palette} = usePageTheme({pageName});
        const {language, isRtl} = usePageLocale({pageName});

        return useMemo(
            () => ({
                styles: StyleSheet.create(
                    getStyles(
                        {
                            theme,
                            colors: {...colors, ...palette},
                            size,
                            shadow,
                            ltrFlexDirection: isRtl ? 'row-reverse' : 'row',
                            contentWidth,
                            width,
                            height,
                            language,
                            isRtl,
                            applyAlpha
                        },
                        ...arg
                    )
                ),
                theme,
                colors: {...colors, ...palette},
                size,
                shadow,
                isRtl,
                width,
                height,
                themeId,
                language
            }),
            [
                theme,
                colors,
                size,
                shadow,
                isRtl,
                themeId,
                palette,
                height,
                width,
                contentWidth,
                language,
                arg,
            ]
        );
    };
};

const applyAlpha = (color = '', alpha) => {
    const alpha256 = (alpha * 255).toFixed();
    const alphaBase16 = Number(alpha256).toString(16); // we're ensuring this is a number then converting
    const paddedAlpha =
      alphaBase16.length === 1 ? alphaBase16.padStart(1, '0') : alphaBase16;
    return color.concat('', paddedAlpha);
  };

export * from './useTheme';
